import { createContext, useContext, useMemo, useReducer } from "react"
import { produce } from "immer"
import { CONTRIBUTION } from "connected-components/compare/position-sets"
import { COMPARISON_MODES, REBALANCED_MODES, SELECTED_MODE_VALUES, END_VS_START_MODES } from "../helpers"
import { usePreferences } from "hooks/use-preferences"
import { useDashboardPreferencesValidation } from "../hooks/preference-validation/use-dashboard-preferences-validation"
import { useModelSync } from "../hooks/use-model-sync"
import { ONE_MONTH } from "helpers/dates"
import { ModelContext } from "./model"
import { SHORT } from "resources/position-sets/composition"

export const DashboardContext = createContext()

const PREFERENCE_SUBTYPE = "dashboard"

const DEFAULT_STATE = {
  normalization: CONTRIBUTION,
  selectedMode: SELECTED_MODE_VALUES.original,
  isAiDrawerOpen: false,
  isOptimizePanelExpanded: false,
  period: ONE_MONTH,
  startDate: null,
  endDate: null,
  earliestDate: null,
  latestDate: null,
  useLatestDate: true,
  performanceStartDate: null,
  performanceEndDate: null,
  isIntraday: false,
  isEndDateIntraday: false,
  intradayBatchMetadata: null,
  isEndVsStartDateComparison: false,
  optimizeConfiguration: null,
  previewOptimizeConfiguration: null,
  instrumentsMetadata: null,
  isBaseOverridden: false, // TODO: [APP-2029] Move isBaseOverridden to its own optimize experiments context
  baseOverride: null, // TODO: [APP-2029] Move baseOverride to its own optimize experiments context
  hasUserAppliedOptimization: false,
}

// Define which fields should be saved as preferences
// [TODO] There are currently two different paradigms when it comes to the following variables:
//        instrument, secondInstrument, comparisonInstrument
//        VS
//        original, rebalanced, comparison
//        We should standardize this.
const PREFERENCE_FIELDS = [
  "instrument",
  "normalization",
  "selectedMode",
  "filter",
  "base",
  "baseOverride",
  "isBaseOverridden",
  "secondInstrument",
  "comparisonInstrument",
  "isOptimizePanelExpanded",
  "period",
  "startDate",
  "endDate",
  "useLatestDate",
  "performanceStartDate",
  "performanceEndDate",
  "isEndVsStartDateComparison",
  "isIntraday",
  "optimizeConfiguration",
  "previewOptimizeConfiguration",
]

// [TODO] Add some tests for this
function getComparisonInstrumentMode(currentMode, currentComparisonInstrument, newComparisonInstrument, currentSecondInstrument) {
  if (COMPARISON_MODES.includes(currentMode) && !newComparisonInstrument) {
    return currentSecondInstrument ? SELECTED_MODE_VALUES.rebalancedVsOriginal : SELECTED_MODE_VALUES.original
  }

  if (!currentComparisonInstrument && newComparisonInstrument) {
    return currentSecondInstrument ? SELECTED_MODE_VALUES.rebalancedVsComparison : SELECTED_MODE_VALUES.originalVsComparison
  }

  return currentMode
}

export function getSecondInstrumentMode(currentMode, currentSecondInstrument, newSecondInstrument, currentComparisonInstrument, overrideMode = null) {
  if (overrideMode) {
    return overrideMode
  }

  // If the current mode is one of the rebalanced modes, but there's no second instrument
  // then the initial selected mode depends on whether there's a comparison instrument
  if (REBALANCED_MODES.includes(currentMode) && !newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.originalVsComparison : SELECTED_MODE_VALUES.original
  }

  // If we previously didn't have a second instrument but now we do,
  // then the initial selected mode depends on whether there's a comparison instrument
  if (!currentSecondInstrument && newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.rebalancedVsComparison : SELECTED_MODE_VALUES.rebalanced
  }

  return currentMode
}

// [TODO] Add some tests for this
function getEndVsStartInstrumentMode(currentMode, currentSecondInstrument, newSecondInstrument, currentComparisonInstrument) {
  if (END_VS_START_MODES.includes(currentMode) && !newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.endVsStart : SELECTED_MODE_VALUES.end
  }

  if (!currentSecondInstrument && newSecondInstrument) {
    return currentComparisonInstrument ? SELECTED_MODE_VALUES.rebalancedVsEnd : SELECTED_MODE_VALUES.rebalanced
  }

  return currentMode
}

function getInstrumentWeight(mainInstrumentMetadata) {
  return mainInstrumentMetadata?.compositionType === SHORT ? -1 : 1
}

function reducer(current, action) {
  return produce(current, draft => {
    switch (action.type) {
      case "setInstrument":
        draft.instrument = action.instrument
        // reset intraday batch information when the instrument changes
        draft.intradayBatchMetadata = null
        // resets filter when the instrument changes
        draft.filter = null
        draft.hasUserAppliedOptimization = false
        break
      // [TODO] Currently, the date can have two different types: moment and string. We should figure out a way to always only have one type or, if that's not possible,
      // using a custom type like { value: String, moment: Moment }
      case "setPeriod":
        if (action.period.startDate && action.period.endDate) {
          draft.startDate = action.period.startDate
          draft.endDate = action.period.endDate

          if (action.period.period) {
            draft.period = action.period.period
          } else if (action.period.startDate && action.period.endDate && !draft.period) {
            draft.period = "CUSTOM"
          }
        // For when period selected is a shortcut
        } else if (typeof action.period === "string") {
          draft.period = action.period
        }
        break
      case "setBoundaryDates":
        draft.earliestDate = action.earliestDate
        draft.latestDate = action.latestDate
        break
      case "setUseLatestDate":
        draft.useLatestDate = action.useLatestDate
        break
      case "setPerformanceStartDate":
        draft.performanceStartDate = action.performanceStartDate
        break
      case "setPerformanceEndDate":
        draft.performanceEndDate = action.performanceEndDate
        break
      case "setBase":
        if (action.base) {
          draft.base = {
            ...action.base,
            weight: getInstrumentWeight(action.mainInstrumentMetadata),
          }
        } else {
          draft.base = action.base
        }
        break
      case "setBaseOverride":
        draft.baseOverride = action.baseOverride
        break
      case "setIsBaseOverridden":
        draft.isBaseOverridden = action.isBaseOverridden
        break
      case "setSecondInstrument":
        draft.selectedMode = draft.isEndVsStartDateComparison
          ? getEndVsStartInstrumentMode(draft.selectedMode, draft.secondInstrument, action.secondInstrument, draft.comparisonInstrument)
          : getSecondInstrumentMode(draft.selectedMode, draft.secondInstrument, action.secondInstrument, draft.comparisonInstrument, action.overrideMode)
        draft.secondInstrument = action.secondInstrument
        draft.secondInstrumentStatus = action.secondInstrumentStatus
        break
      case "setComparisonInstrument":
        draft.selectedMode = draft.isEndVsStartDateComparison
          ? getEndVsStartInstrumentMode(draft.selectedMode, draft.secondInstrument, action.secondInstrument, draft.comparisonInstrument)
          : getComparisonInstrumentMode(draft.selectedMode, draft.comparisonInstrument, action.instrument, draft.secondInstrument)
        draft.comparisonInstrument = action.instrument
        if (!action.instrument) {
          draft.hasUserAppliedOptimization = false
        }
        break
      case "setIsEndVsStartDateComparison":
        draft.isEndVsStartDateComparison = action.isEndVsStartDateComparison
        break
      case "setFilter":
        draft.filter = action.filter
        break
      case "setNormalization":
        draft.normalization = action.normalization
        break
      case "setSelectedMode":
        draft.selectedMode = action.selectedMode
        break
      case "setIsAiDrawerOpen":
        draft.isAiDrawerOpen = action.isAiDrawerOpen
        break
      case "setIsOptimizePanelExpanded":
        draft.isOptimizePanelExpanded = action.isOptimizePanelExpanded
        break
      case "setPreferences":
        if (action.preferences) {
          Object.assign(draft, action.preferences)
        }
        break
      case "setIsIntraday":
        draft.isIntraday = action.isIntraday
        if (!action.isIntraday) {
          draft.intradayBatchMetadata = null
        }
        break
      case "setIsEndDateIntraday":
        draft.isEndDateIntraday = action.isEndDateIntraday
        break
      case "setIntradayBatchMetadata":
        draft.intradayBatchMetadata = action.intradayBatchMetadata
        break
      case "setOptimizeConfiguration":
        draft.optimizeConfiguration = action.optimizeConfiguration
        break
      case "setOptimizeConfigurationFromUser":
        draft.optimizeConfiguration = action.optimizeConfiguration
        draft.hasUserAppliedOptimization = true
        break
      case "setPreviewOptimizeConfiguration":
        draft.previewOptimizeConfiguration = action.previewOptimizeConfiguration
        break
      case "setInstrumentsMetadata":
        draft.instrumentsMetadata = action.instrumentsMetadata
        break
    }
  })
}

function setInstrument(instrument) {
  return {
    type: "setInstrument",
    instrument,
  }
}

function setPeriod(period) {
  return {
    type: "setPeriod",
    period,
  }
}

function setBoundaryDates(earliestDate, latestDate) {
  return {
    type: "setBoundaryDates",
    earliestDate,
    latestDate,
  }
}

function setUseLatestDate(useLatestDate) {
  return {
    type: "setUseLatestDate",
    useLatestDate,
  }
}

function setPerformanceStartDate(performanceStartDate) {
  return {
    type: "setPerformanceStartDate",
    performanceStartDate,
  }
}

function setPerformanceEndDate(performanceEndDate) {
  return {
    type: "setPerformanceEndDate",
    performanceEndDate,
  }
}

function setBase(base, mainInstrumentMetadata) {
  return {
    type: "setBase",
    base,
    mainInstrumentMetadata,
  }
}

function setBaseOverride(baseOverride) {
  return {
    type: "setBaseOverride",
    baseOverride,
  }
}

function setIsBaseOverridden(isBaseOverridden) {
  return {
    type: "setIsBaseOverridden",
    isBaseOverridden,
  }
}

function setSecondInstrument(secondInstrument, secondInstrumentStatus, overrideMode = null) {
  return {
    type: "setSecondInstrument",
    secondInstrument,
    secondInstrumentStatus,
    overrideMode,
  }
}

function setFilter(filter) {
  return {
    type: "setFilter",
    filter,
  }
}

function setNormalization(normalization) {
  return {
    type: "setNormalization",
    normalization,
  }
}

function setComparisonInstrument(instrument) {
  return {
    type: "setComparisonInstrument",
    instrument,
  }
}

function setIsEndVsStartDateComparison(isEndVsStartDateComparison) {
  return {
    type: "setIsEndVsStartDateComparison",
    isEndVsStartDateComparison,
  }
}

function setSelectedMode(selectedMode) {
  return {
    type: "setSelectedMode",
    selectedMode,
  }
}

function setIsAiDrawerOpen(isAiDrawerOpen) {
  return {
    type: "setIsAiDrawerOpen",
    isAiDrawerOpen,
  }
}

function setIsOptimizePanelExpanded(isOptimizePanelExpanded) {
  return {
    type: "setIsOptimizePanelExpanded",
    isOptimizePanelExpanded,
  }
}

function setIsIntraday(isIntraday) {
  return {
    type: "setIsIntraday",
    isIntraday,
  }
}

function setIsEndDateIntraday(isEndDateIntraday) {
  return {
    type: "setIsEndDateIntraday",
    isEndDateIntraday,
  }
}

function setIntradayBatchMetadata(intradayBatchMetadata) {
  return {
    type: "setIntradayBatchMetadata",
    intradayBatchMetadata,
  }
}

function setPreferences(preferences) {
  return {
    type: "setPreferences",
    preferences,
  }
}

function setOptimizeConfiguration(optimizeConfiguration) {
  return {
    type: "setOptimizeConfiguration",
    optimizeConfiguration,
  }
}

function setOptimizeConfigurationFromUser(optimizeConfiguration) {
  return {
    type: "setOptimizeConfigurationFromUser",
    optimizeConfiguration,
  }
}

function setPreviewOptimizeConfiguration(previewOptimizeConfiguration) {
  return {
    type: "setPreviewOptimizeConfiguration",
    previewOptimizeConfiguration,
  }
}

function setInstrumentsMetadata(instrumentsMetadata) {
  return {
    type: "setInstrumentsMetadata",
    instrumentsMetadata,
  }
}

export default function useContextDashboard() {
  const { modelId, setModelId } = useContext(ModelContext)
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE)
  const setPreferenceValidationOptions = useDashboardPreferencesValidation(modelId)

  const isPreferencesLoaded = usePreferences(
    PREFERENCE_SUBTYPE,
    PREFERENCE_FIELDS,
    state,
    dispatch,
    setPreferences,
    { setPreferenceValidationOptions },
  )

  // Sync dashboard model ID with global home layout state to prevent infinite renders
  useModelSync(modelId)

  const contextUpdaters = useMemo(() => ({
    // [TODO:TS] Consider pattern: setInstrument: (...args: Parameters<typeof setInstrument>) => void;
    setInstrument: (...args) => dispatch(setInstrument(...args)),
    setPeriod: (...args) => dispatch(setPeriod(...args)),
    setBoundaryDates: (...args) => dispatch(setBoundaryDates(...args)),
    setUseLatestDate: (...args) => dispatch(setUseLatestDate(...args)),
    setPerformanceStartDate: (...args) => dispatch(setPerformanceStartDate(...args)),
    setPerformanceEndDate: (...args) => dispatch(setPerformanceEndDate(...args)),
    setBase: (...args) => dispatch(setBase(...args)),
    setBaseOverride: (...args) => dispatch(setBaseOverride(...args)),
    setIsBaseOverridden: (...args) => dispatch(setIsBaseOverridden(...args)),
    setSecondInstrument: (...args) => dispatch(setSecondInstrument(...args)),
    setFilter: (...args) => dispatch(setFilter(...args)),
    setNormalization: (...args) => dispatch(setNormalization(...args)),
    setComparisonInstrument: (...args) => dispatch(setComparisonInstrument(...args)),
    setIsEndVsStartDateComparison: (...args) => dispatch(setIsEndVsStartDateComparison(...args)),
    setSelectedMode: (...args) => dispatch(setSelectedMode(...args)),
    setIsAiDrawerOpen: (...args) => dispatch(setIsAiDrawerOpen(...args)),
    setIsOptimizePanelExpanded: (...args) => dispatch(setIsOptimizePanelExpanded(...args)),
    setIsIntraday: (...args) => dispatch(setIsIntraday(...args)),
    setIsEndDateIntraday: (...args) => dispatch(setIsEndDateIntraday(...args)),
    setIntradayBatchMetadata: (...args) => dispatch(setIntradayBatchMetadata(...args)),
    setOptimizeConfiguration: (...args) => dispatch(setOptimizeConfiguration(...args)),
    setOptimizeConfigurationFromUser: (...args) => dispatch(setOptimizeConfigurationFromUser(...args)),
    setPreviewOptimizeConfiguration: (...args) => dispatch(setPreviewOptimizeConfiguration(...args)),
    setInstrumentsMetadata: (...args) => dispatch(setInstrumentsMetadata(...args)),
    setModelId,
  }), [dispatch, setModelId])

  const contextValue = useMemo(() => ({
    ...state,
    ...contextUpdaters,
    modelId,
    isPreferencesLoaded,
  }), [state, contextUpdaters, modelId, isPreferencesLoaded])

  return [DashboardContext, contextValue]
}
